/**
 * Masking helpers for showing customer data to affiliates without revealing it.
 */

/** Second-level public suffixes treated as one suffix (e.g. "co.ir"). */
const MULTI_LABEL_SUFFIXES = new Set([
  'co.ir', 'ac.ir', 'org.ir', 'net.ir', 'gov.ir', 'id.ir', 'sch.ir',
  'co.uk', 'org.uk', 'ac.uk', 'com.tr', 'net.tr', 'org.tr', 'com.au', 'co.com',
]);

const MASK = '***';

/**
 * Mask a domain for display: "example.ir" -> "exa***.ir".
 *
 * - Subdomains are dropped ("shop.example.co.ir" -> "exa***.co.ir").
 * - Known two-label suffixes ("co.ir", "ac.ir", ...) are kept whole.
 * - Visible prefix depends on name length: 6+ chars show 3, 4-5 show 2,
 *   2-3 show 1, a single character shows none. The mask is always "***",
 *   so the real length is not revealed.
 * - Input without a dot, or empty input, returns "***".
 */
export function maskDomain(domain: string): string {
  const clean = domain.trim().toLowerCase().replace(/^\*\./, '').replace(/\.+$/, '');
  const labels = clean.split('.').filter((l) => l.length > 0);
  if (labels.length < 2) return MASK;
  const lastTwo = labels.slice(-2).join('.');
  const suffixLabels = labels.length >= 3 && MULTI_LABEL_SUFFIXES.has(lastTwo) ? 2 : 1;
  const suffix = labels.slice(-suffixLabels).join('.');
  const name = Array.from(labels[labels.length - suffixLabels - 1] as string);
  const visible = name.length >= 6 ? 3 : name.length >= 4 ? 2 : name.length >= 2 ? 1 : 0;
  return `${name.slice(0, visible).join('')}${MASK}.${suffix}`;
}

/**
 * Mask an e-mail address for display: "sara.mohammadi@example.com" ->
 * "sa***@example.com". The domain stays readable (the person recognises
 * which mailbox it is); the local part shows at most its first 2 characters.
 * Anything without an "@" returns "***".
 */
export function maskEmail(email: string): string {
  const clean = email.trim();
  const at = clean.lastIndexOf('@');
  if (at < 1 || at === clean.length - 1) return MASK;
  const local = Array.from(clean.slice(0, at));
  const visible = local.length >= 4 ? 2 : 1;
  return `${local.slice(0, visible).join('')}${MASK}@${clean.slice(at + 1).toLowerCase()}`;
}
